import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import './CommandPalette.css';

/**
 * CommandPalette — ⌘K / Ctrl+K fuzzy launcher.
 *
 * Jump to any page or run a quick appearance action without hunting through the
 * nav. Opens on Cmd/Ctrl+K (or "/"), filters as you type, navigates on Enter.
 * Implements docs/frontend/DESIGN_REDESIGN.md §6.3.
 *
 * @param {function} onNavigate
 * @param {boolean} isAdmin
 * @param {boolean} isModerator
 * @param {boolean} isContentCreator
 */
function CommandPalette({ onNavigate, isAdmin, isModerator, isContentCreator }) {
  const { toggleMode, setDensity, densityId, themes, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  const commands = useMemo(() => {
    const nav = [
      { id: 'home', label: 'Home', icon: '🏠', kind: 'Page' },
      { id: 'collection', label: 'Collection', icon: '🎴', kind: 'Page' },
      { id: 'trades', label: 'Trade', icon: '🔄', kind: 'Page' },
      { id: 'expeditions-hub', label: 'Expeditions', icon: '🗺️', kind: 'Page' },
      { id: 'minigames', label: 'Mini games', icon: '🎮', kind: 'Page' },
      { id: 'gallery', label: 'Gallery', icon: '🖼️', kind: 'Page' },
      { id: 'galleries', label: 'Galleries', icon: '🖼️', kind: 'Page' },
      { id: 'reference', label: 'Reference', icon: '📖', kind: 'Page' },
      { id: 'proposals', label: 'Suggestions', icon: '💡', kind: 'Page' },
      { id: 'shop', label: 'Shop', icon: '🛒', kind: 'Page' },
      { id: 'profile', label: 'Profile', icon: '👤', kind: 'Page' }
    ];
    const staff = [];
    if (isAdmin || isModerator) {
      staff.push({ id: 'stats', label: 'Stats', icon: '📊', kind: 'Staff' });
      staff.push({ id: 'moderator', label: 'Moderator Tools', icon: '⚙️', kind: 'Staff' });
      staff.push({ id: 'proposals-admin', label: 'Review Suggestions', icon: '🔧', kind: 'Staff' });
      staff.push({ id: 'task-board', label: 'Task Board', icon: '📋', kind: 'Staff' });
    } else if (isContentCreator) {
      staff.push({ id: 'moderator', label: 'Content Tools', icon: '⚙️', kind: 'Staff' });
      staff.push({ id: 'proposals-admin', label: 'Review Suggestions', icon: '🔧', kind: 'Staff' });
    }
    if (isAdmin) {
      staff.push({ id: 'credits-admin', label: 'Credits', icon: '💰', kind: 'Staff' });
      staff.push({ id: 'admin-tools', label: 'Admin Tools', icon: '🔐', kind: 'Staff' });
      staff.push({ id: 'design-lab', label: 'Design Lab', icon: '🎨', kind: 'Staff' });
      staff.push({ id: 'production-proposals', label: 'Prod Proposals', icon: '🔄', kind: 'Staff' });
    }
    // Note: the palette intentionally lists more page destinations than the
    // curated Sidebar rail (e.g. gallery/shop/profile), so the two nav lists are
    // not a 1:1 shared constant. Keep staff destinations in sync with Sidebar.js.
    const actions = [
      { id: 'act:toggle-mode', label: 'Toggle light / dark mode', icon: '🌓', kind: 'Action', run: toggleMode },
      {
        id: 'act:toggle-density',
        label: `Density: switch to ${densityId === 'compact' ? 'comfortable' : 'compact'}`,
        icon: '↕️',
        kind: 'Action',
        run: () => setDensity(densityId === 'compact' ? 'comfortable' : 'compact')
      },
      ...themes.map((t) => ({
        id: `act:theme:${t.id}`,
        label: `Theme: ${t.name}`,
        icon: '🎨',
        kind: 'Theme',
        run: () => setTheme(t.id)
      }))
    ];
    return [...nav, ...staff, ...actions];
  }, [isAdmin, isModerator, isContentCreator, toggleMode, setDensity, densityId, themes, setTheme]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => c.label.toLowerCase().includes(q) || c.kind.toLowerCase().includes(q));
  }, [query, commands]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  }, []);

  const runCommand = useCallback(
    (cmd) => {
      if (!cmd) return;
      if (cmd.run) {
        cmd.run();
      } else {
        onNavigate(cmd.id);
      }
      close();
    },
    [onNavigate, close]
  );

  // Global hotkey: Cmd/Ctrl+K toggles the palette; "/" opens it (unless the user
  // is typing in a field, where "/" is a normal character).
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
    };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === '/' && !open && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) return null;

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runCommand(results[activeIndex]);
    }
  };

  return (
    <div className="cmdk-backdrop" onMouseDown={close}>
      <div className="cmdk" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Command palette">
        <input
          ref={inputRef}
          className="cmdk-input"
          placeholder="Jump to a page or run an action…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls="cmdk-list"
          aria-autocomplete="list"
          aria-activedescendant={results.length ? `cmdk-opt-${activeIndex}` : undefined}
          aria-label="Search commands"
        />
        <ul className="cmdk-list" id="cmdk-list" role="listbox">
          {results.length === 0 && <li className="cmdk-empty">No matches</li>}
          {results.map((cmd, i) => (
            // Option lives on the <li>; focus stays in the input and
            // aria-activedescendant tracks the highlighted option (combobox pattern).
            <li
              key={cmd.id}
              id={`cmdk-opt-${i}`}
              role="option"
              aria-selected={i === activeIndex}
              className={`cmdk-item ${i === activeIndex ? 'cmdk-item-active' : ''}`}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => runCommand(cmd)}
            >
              <span className="cmdk-item-icon">{cmd.icon}</span>
              <span className="cmdk-item-label">{cmd.label}</span>
              <span className="cmdk-item-kind">{cmd.kind}</span>
            </li>
          ))}
        </ul>
        <div className="cmdk-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
